import {
  createSystem,
  defaultConfig,
  defineConfig,
  defineRecipe,
  defineSlotRecipe,
} from '@chakra-ui/react'
import { Global } from '@emotion/react'

import backgroundDark from 'assets/background-dark.png'
import backgroundLight from 'assets/background-light.png'
import eot from 'assets/fonts/beaded.eot'
import svg from 'assets/fonts/beaded.svg'
import ttf from 'assets/fonts/beaded.ttf'
import woff from 'assets/fonts/beaded.woff'

export const Fonts = () => (
  <Global
    styles={`
		@font-face {
			font-family: "beaded";
			src:url(${eot});
			src:url("assets/fonts/beaded.eot?#iefix") format("embedded-opentype"),
				url(${woff}) format("woff"),
				url(${ttf}) format("truetype"),
				url(${svg}#beaded) format("svg");
			font-weight: normal;
			font-style: normal;
		}
	`}
  />
)

export const config = defineConfig({
  globalCss: {
    html: {
      colorPalette: 'gray',
      bgColor: 'var(--bc-bg)',
      '@media print': { bgColor: 'white' },
    },

    // -- BeadedCloud design tokens --
    ':root': {
      // Font stacks
      '--bc-font':
        '"Geist", -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif',
      '--bc-mono': '"Geist Mono", ui-monospace, "SF Mono", Menlo, monospace',
      // Brand primitives (mode-invariant)
      '--bc-blue-bright': '#1672dc',
      '--bc-blue-deep': '#06549b',
      // Geometry
      '--bc-r-sm': '6px',
      '--bc-r-md': '10px',
      '--bc-r-lg': '14px',
      '--bc-r-xl': '20px',
      // Light-mode semantic tokens
      '--bc-bg': '#f5f4f0',
      '--bc-surface': '#ffffff',
      '--bc-surface-2': '#fafaf7',
      '--bc-line': '#e6e3dc',
      '--bc-line-strong': '#d6d2c8',
      '--bc-ink': '#14181f',
      '--bc-ink-fg': '#ffffff',
      '--bc-ink-2': '#3a4150',
      '--bc-ink-3': '#6b7280',
      '--bc-ink-4': '#9aa0aa',
      '--bc-blue': '#1672dc',
      '--bc-blue-ink': '#06549b',
      '--bc-blue-tint': '#e6f0fb',
      '--bc-blue-line': '#c6dcf4',
      // 4.13:1 on --bc-surface -- status icons/tinted-bg use only, not body text on white
      '--bc-green': '#2f8c5b',
      '--bc-green-tint': '#e3f3ea',
      // 3.66:1 on --bc-surface -- status icons/tinted-bg use only, not body text on white
      '--bc-amber': '#b67912',
      '--bc-amber-tint': '#faecd0',
      '--bc-red': '#c2402b',
      '--bc-red-tint': '#fadfd8',
      '--bc-draft-tint': '#eeece6',
      '--bc-topo-ring': '#d4cfc5',
      '--bc-shadow-card': '0 1px 2px rgba(0,0,0,0.04)',
      // Applied here to avoid SystemStyleObject vendor-prefix constraints
      WebkitFontSmoothing: 'antialiased',
      MozOsxFontSmoothing: 'grayscale',
    } as any, // eslint-disable-line @typescript-eslint/no-explicit-any
    '.dark': {
      '--bc-bg': '#1a1a1a',
      '--bc-surface': '#242424',
      '--bc-surface-2': '#2c2c2c',
      '--bc-line': '#383838',
      '--bc-line-strong': '#4a4a4a',
      '--bc-ink': '#f5f5f5',
      '--bc-ink-fg': '#1a1a1a',
      '--bc-ink-2': '#d0d0d0',
      '--bc-ink-3': '#989898',
      // 3.13:1 on --bc-surface -- decorative/disabled only, not for text or placeholder
      '--bc-ink-4': '#707070',
      '--bc-blue': '#6cb0fa',
      '--bc-blue-ink': '#9ec9fb',
      '--bc-blue-tint': '#2a2f36',
      '--bc-blue-line': '#3a4250',
      '--bc-green': '#7cc99c',
      '--bc-green-tint': '#232b26',
      '--bc-amber': '#dcad60',
      '--bc-amber-tint': '#2e2924',
      '--bc-red': '#ea9281',
      '--bc-red-tint': '#2e2522',
      '--bc-draft-tint': '#2e2e2a',
      '--bc-topo-ring': '#2f2f2f',
      '--bc-shadow-card': '0 1px 2px rgba(0,0,0,0.4)',
    },

    // Force light-mode ink tokens on elements that always render on a white
    // background regardless of the app color mode (e.g. Google Maps InfoWindow).
    '.bc-on-white': {
      '--bc-ink': '#14181f',
      '--bc-ink-2': '#3a4150',
      '--bc-ink-3': '#6b7280',
      '--bc-ink-4': '#9aa0aa',
    },

    '.bc-topo': {
      position: 'relative',
      overflow: 'hidden',
    },
    '.bc-topo::before': {
      content: '""',
      position: 'absolute',
      inset: 0,
      backgroundImage: [
        'radial-gradient(circle at 88% 12%, transparent 25%, var(--bc-topo-ring) 25%, var(--bc-topo-ring) 26.5%, transparent 26.5%)',
        'radial-gradient(circle at 88% 12%, transparent 40%, var(--bc-topo-ring) 40%, var(--bc-topo-ring) 41.5%, transparent 41.5%)',
        'radial-gradient(circle at 88% 12%, transparent 55%, var(--bc-topo-ring) 55%, var(--bc-topo-ring) 56.5%, transparent 56.5%)',
        'radial-gradient(circle at 88% 12%, transparent 70%, var(--bc-topo-ring) 70%, var(--bc-topo-ring) 71.5%, transparent 71.5%)',
        'radial-gradient(circle at 88% 12%, transparent 85%, var(--bc-topo-ring) 85%, var(--bc-topo-ring) 86.5%, transparent 86.5%)',
      ].join(', '),
      pointerEvents: 'none',
      zIndex: 0,
    },

    // @keyframes and other CSS at-rules are not in SystemStyleObject; as any required
    '@keyframes bc-shimmer': {
      '0%': { opacity: 0.5 },
      '100%': { opacity: 1 },
    } as any, // eslint-disable-line @typescript-eslint/no-explicit-any
    '@keyframes bc-spin': {
      '0%': { transform: 'rotate(0deg)' },
      '100%': { transform: 'rotate(360deg)' },
    } as any, // eslint-disable-line @typescript-eslint/no-explicit-any

    // @page at-rule not in SystemStyleObject
    '@page': {
      size: 'auto',
    } as any, // eslint-disable-line @typescript-eslint/no-explicit-any
    body: {
      bgColor: 'transparent',
      overscrollBehaviorY: 'none',
      fontFamily: 'var(--bc-font)',
      fontSmooth: 'antialiased',
      fontSynthesis: 'none',
      letterSpacing: '-0.005em',
      fontFeatureSettings: '"ss01", "ss02", "cv11"',
    },

    // Topo background -- modern UI only, only when logged in (class added by BaseLayout)
    'html.bc-topo-bg::before': {
      content: '""',
      position: 'fixed',
      inset: '0',
      backgroundImage: "url('/topo-bg.jpg')",
      backgroundSize: 'cover',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      filter: 'invert(1)',
      opacity: 0.06,
      pointerEvents: 'none',
      zIndex: -1,
    },
    'html.bc-topo-bg.dark::before': {
      filter: 'none',
      opacity: 0.07,
    },
    // nested @media not in SystemStyleObject
    '@media print': {
      'html.bc-topo-bg::before': { display: 'none' },
    } as any, // eslint-disable-line @typescript-eslint/no-explicit-any
    '::-webkit-scrollbar': { width: '10px', height: '10px' },
    '::-webkit-scrollbar-track': { background: 'transparent' },
    '::-webkit-scrollbar-thumb': {
      background: 'var(--bc-line-strong)',
      borderRadius: '10px',
      border: '2px solid var(--bc-surface)',
    },
    '.markdown *': {
      all: 'revert',
    },
    '#sentry-feedback': {
      '--background': '#F7FAFC',
      '--background-hover': '#E2E8F0',
      '--foreground': '#1A202C',
      '--border': '0.5px solid #4A5568',
      '--submit-background': '#1A202C',
      '--submit-background-hover': '#1A202C',
      '--submit-border': '#1A202C',
      '--submit-outline-focus': '#1A202C',
      '--submit-foreground': '#F7FAFC',
      '--submit-foreground-hover': '#F7FAFC',
      '--cancel-background': '#F7FAFC',
      '--cancel-background-hover': '#F7FAFC',
      '--input-background': '#F7FAFC',
      '--input-foreground': '#1A202C',
      '--input-outline-focus': '#1A202C',
      '.dark &': {
        '--background': '#2D3748',
        '--background-hover': '#1A202C',
        '--foreground': '#F7FAFC',
        '--border': '0.5px solid #E2E8F0',
        '--submit-background': '#F7FAFC',
        '--submit-background-hover': '#F7FAFC',
        '--submit-border': '#F7FAFC',
        '--submit-outline-focus': '#F7FAFC',
        '--submit-foreground': '#1A202C',
        '--submit-foreground-hover': '#1A202C',
        '--cancel-background': '#2D3748',
        '--cancel-background-hover': '#2D3748',
        '--input-background': '#2D3748',
        '--input-foreground': '#F7FAFC',
        '--input-outline-focus': '#F7FAFC',
      },
    },
    // Chakra v3 sets ::selection bg to colorPalette.emphasized/80, which
    // resolves to gray.700 in dark mode -- the same as our card/body bg,
    // making text selection invisible.
    '*::selection': {
      bg: 'blue.200',
      color: 'black',
      _dark: { bg: 'blue.600', color: 'white' },
    },
    '.bulk-export-checkbox + span': {
      borderWidth: '3px',
      borderColor: 'gray.500',
    },
    // Column-group highlight (e.g. "Last Transmission" header). Uses bc token
    // instead of gray.200/_dark:gray.600 so it stays on-theme in both modes.
    '.table-col-gray': {
      bg: 'var(--bc-surface-2)',
    },
    'table td, table th': {
      borderColor: 'var(--bc-line)',
    },
    // Button link variant nested selectors -- moved from button recipe
    // because v3 defineRecipe doesn't support arbitrary CSS nesting.
    '.chakra-button .gr-icon path': {
      stroke: '#1A202C',
      '.dark &': {
        stroke: 'rgba(255, 255, 255, 0.92)',
      },
    },
    '.chakra-button .beta-button path': {
      stroke: 'rgb(192, 86, 33)',
      color: 'rgb(192, 86, 33)',
      '.dark &': {
        stroke: 'rgb(251, 211, 141)',
        color: 'rgb(251, 211, 141)',
      },
    },

    // Classic UI mode -- applied when <html class="bc-classic">
    // Restores the pre-redesign visual appearance: background image,
    // system font stack, and Chakra gray palette tokens.
    // CSS custom properties (--bc-*) are not in SystemStyleObject; as any required
    'html.bc-classic': {
      '--bc-font':
        '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
      '--bc-mono':
        'SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
      WebkitFontSmoothing: 'auto',
      MozOsxFontSmoothing: 'auto',
      '--bc-bg': 'transparent',
      '--bc-surface': '#ffffff',
      '--bc-surface-2': '#f7fafc',
      '--bc-line': '#e2e8f0',
      '--bc-line-strong': '#cbd5e0',
      '--bc-ink': '#1a202c',
      '--bc-ink-fg': '#ffffff',
      '--bc-ink-2': '#4a5568',
      '--bc-ink-3': '#718096',
      '--bc-ink-4': '#a0aec0',
      '--bc-blue': '#1672dc',
      '--bc-blue-ink': '#06549b',
      '--bc-blue-tint': '#ebf4ff',
      '--bc-blue-line': '#bee3f8',
      '--bc-green': '#38a169',
      '--bc-green-tint': '#f0fff4',
      '--bc-amber': '#d69e2e',
      '--bc-amber-tint': '#fffff0',
      '--bc-red': '#e53e3e',
      '--bc-red-tint': '#fff5f5',
      '--bc-draft-tint': '#f7fafc',
      '--bc-shadow-card': '0 1px 3px rgba(0,0,0,0.12)',
    } as any, // eslint-disable-line @typescript-eslint/no-explicit-any
    'html.bc-classic body': {
      bgColor: 'transparent',
      bgImage: `url('${backgroundLight}')`,
      bgSize: 'cover',
      bgAttachment: 'fixed',
      bgRepeat: 'no-repeat',
      backgroundPosition: 'center',
      fontFamily:
        '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
      letterSpacing: 'normal',
      fontFeatureSettings: 'normal',
      fontSynthesis: 'auto',
      // Chrome prints background images when "Background graphics" is on
      '@media print': { bgImage: 'none' },
    },
    // CSS custom properties (--bc-*) are not in SystemStyleObject; as any required
    'html.bc-classic.dark': {
      '--bc-bg': 'transparent',
      '--bc-surface': '#2d3748',
      '--bc-surface-2': '#1a202c',
      '--bc-line': '#4a5568',
      '--bc-line-strong': '#718096',
      '--bc-ink': '#f7fafc',
      '--bc-ink-fg': '#1a202c',
      '--bc-ink-2': '#e2e8f0',
      '--bc-ink-3': '#a0aec0',
      '--bc-ink-4': '#718096',
    } as any, // eslint-disable-line @typescript-eslint/no-explicit-any
    'html.bc-classic.dark body': {
      bgImage: `url('${backgroundDark}')`,
      // Outranks the print override above, so it needs its own
      '@media print': { bgImage: 'none' },
    },
  },

  theme: {
    tokens: {
      colors: {
        // Override gray palette to match Chakra v1 blue-tinted grays.
        // v3 ships neutral/zinc grays which look different from v1.
        gray: {
          50: { value: '#F7FAFC' },
          100: { value: '#EDF2F7' },
          200: { value: '#E2E8F0' },
          300: { value: '#CBD5E0' },
          400: { value: '#A0AEC0' },
          500: { value: '#718096' },
          600: { value: '#4A5568' },
          700: { value: '#2D3748' },
          800: { value: '#1A202C' },
          900: { value: '#171923' },
          950: { value: '#0d0f14' },
        },
        brand: {
          100: { value: '#1772dd' },
          200: { value: '#07559B' },
        },
        // Status colors for modern components (maps to --bc-* vars)
        status: {
          active: { value: 'var(--bc-green)' },
          activeTint: { value: 'var(--bc-green-tint)' },
          warn: { value: 'var(--bc-amber)' },
          warnTint: { value: 'var(--bc-amber-tint)' },
          offline: { value: 'var(--bc-red)' },
          offlineTint: { value: 'var(--bc-red-tint)' },
        },
      },
      fonts: {
        beadedFont: { value: 'beaded' },
        heading: { value: 'var(--bc-font)' },
        body: { value: 'var(--bc-font)' },
        mono: { value: 'var(--bc-mono)' },
      },
    },

    semanticTokens: {
      colors: {
        // v3 defaults bg to black in dark mode; override to match card backgrounds.
        bg: {
          DEFAULT: {
            value: {
              _light: 'white',
              _dark: '{colors.gray.700}',
            },
          },
          // v3 bg.panel defaults to gray.950 in dark mode (used by Dialog, Card, etc.)
          panel: {
            value: {
              _light: '{colors.white}',
              _dark: '{colors.gray.700}',
            },
          },
        },
        // Override default border color so inputs/controls are visible in dark mode.
        border: {
          DEFAULT: {
            value: {
              _light: '{colors.gray.200}',
              _dark: '{colors.whiteAlpha.300}',
            },
          },
        },
        // Override gray solid/contrast to match Chakra v1 button defaults.
        // v1: bg=gray.100 (#EDF2F7), hover=gray.200 (#E2E8F0), text=gray.800.
        gray: {
          solid: {
            value: {
              _light: '{colors.gray.100}',
              _dark: '{colors.whiteAlpha.200}',
            },
          },
          contrast: {
            value: {
              _light: '{colors.gray.800}',
              _dark: '{colors.whiteAlpha.900}',
            },
          },
        },
      },
    },

    layerStyles: {
      menuBar: {
        value: {
          bg: 'white',
          _dark: { bg: 'gray.700' },
          boxShadow: 'md',
          '@media print': {
            display: 'none',
          },
        },
      },
      adminMenuBar: {
        value: {
          bg: 'white',
          _dark: { bg: 'gray.700' },
          boxShadow:
            '0 4px 6px -1px rgb(255 0 0 / 70%), 0 2px 4px -1px rgb(255 0 0 / 42%)',
          '@media print': {
            display: 'none',
          },
        },
      },
      card: {
        value: {
          bg: 'white',
          _dark: { bg: 'gray.700' },
          p: '3',
          boxShadow: 'md',
          borderRadius: 'md',
          maxWidth: 'calc(100vw - 6rem)',
          width: 'calc(100% - 3rem)',
          '@media print': {
            width: '100vw',
            maxWidth: 'unset',
            boxShadow: 'none',
          },
          opacity: 0.99,
        },
      },
      cardContainer: {
        value: {
          mt: '2rem',
          mb: '3.2rem',
          '@media print': {
            width: '100vw',
            maxWidth: 'unset',
            m: '0',
          },
        },
      },
      mobileCard: {
        value: {
          bg: 'white',
          _dark: { bg: 'gray.700' },
          p: '3',
          boxShadow: 'md',
          borderRadius: 'md',
          maxWidth: '70rem',
          width: 'calc(100% - 1rem)',
          '@media print': {
            width: '100vw',
            maxWidth: 'unset',
            boxShadow: 'none',
          },
          opacity: 0.99,
        },
      },
      mobileCardContainer: {
        value: {
          mt: '0.5rem',
          mb: '0.5rem',
          '@media print': {
            width: '100vw',
            maxWidth: 'unset',
            m: '0',
          },
        },
      },
      heading: {
        value: {
          fontSize: 'xl',
        },
      },
      centeredLoading: {
        value: {
          // Centers within the nearest positioned ancestor (the BaseLayout
          // content wrapper tracks the content area for any sidebar width).
          // Pre-layout (login, auth bootstrap) the containing block is the
          // viewport.
          left: '50%',
          top: '20vh',
          transform: 'translateX(-50%)',
          position: 'absolute',
        },
      },
      beadedFont: {
        value: {
          fontFamily: 'beaded',
        },
      },
      customScrollbar: {
        value: {
          '::-webkit-scrollbar': { height: '10px' },
          '::-webkit-scrollbar-track': {
            bg: 'gray.50',
            boxShadow: 'inset 0px 2.5rem 0 0 #F7FAFC',
            _dark: {
              bg: 'gray.800',
              boxShadow: 'inset 0px 2.5rem 0 0 #1A202C',
            },
            border: '0px solid transparent',
          },
        },
      },
      customSelect: {
        value: {
          color: '#1A202C',
          _dark: { color: 'rgba(255, 255, 255, 0.92)' },
        },
      },
      printStyleForSelect: {
        value: {
          '@media print': {
            border: 'none',
            appearance: 'none',
            WebkitAppearance: 'none',
            MozAppearance: 'none',
            padding: '0',
            margin: '0',
            color: '#1A202C',
            '.dark &': { color: 'rgba(255, 255, 255, 0.92)' },
          },
        },
      },
      printStyleForInput: {
        value: {
          '@media print': {
            border: 'none',
            color: '#1A202C',
            '.dark &': { color: 'rgba(255, 255, 255, 0.92)' },
            resize: 'none',
          },
        },
      },
      printStyleForInputPadding: {
        value: {
          '@media print': {
            border: 'none',
            bg: 'white',
            color: '#1A202C',
            '.dark &': { color: 'rgba(255, 255, 255, 0.92)' },
          },
        },
      },
      printStyleForNumberInput: {
        value: {
          '@media print': {
            border: 'none',
            color: '#1A202C',
            '.dark &': { color: 'rgba(255, 255, 255, 0.92)' },
            textAlign: 'right',
          },
        },
      },
      printStyleForRadioInput: {
        value: {
          '@media print': {
            'div[class^=chakra-radio__]': {
              display: 'none',
            },
            '.chakra-radio__label[data-checked]': {
              display: 'inline-flex',
            },
          },
        },
      },
      hideOnPrint: {
        value: {
          '@media print': {
            display: 'none',
          },
        },
      },
      hideOnNotPrint: {
        value: {
          '@media not print': {
            display: 'none',
          },
        },
      },

      // -- Modern UI (bc-) layer styles --
      // Classic components use the entries above. Modern components use these.
      // All reference --bc-* CSS custom properties so they automatically
      // adapt when html.bc-classic or html.dark overrides the tokens.
      'bc-card': {
        value: {
          bg: 'var(--bc-surface)',
          borderRadius: 'var(--bc-r-lg)',
          border: '1px solid var(--bc-line)',
          boxShadow: 'var(--bc-shadow-card)',
        },
      },
      'bc-card-flat': {
        value: {
          bg: 'var(--bc-surface)',
          borderRadius: 'var(--bc-r-lg)',
          border: '1px solid var(--bc-line)',
        },
      },
      'bc-tile': {
        value: {
          bg: 'var(--bc-surface)',
          borderRadius: 'var(--bc-r-md)',
          border: '1px solid var(--bc-line)',
        },
      },
      'bc-surface-2': {
        value: {
          bg: 'var(--bc-surface-2)',
        },
      },
      'bc-section-header': {
        value: {
          fontSize: '11px',
          fontWeight: 600,
          color: 'var(--bc-ink-3)',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          lineHeight: 1,
        },
      },
    },

    textStyles: {
      // -- Modern UI (bc-) text styles --
      'bc-label': {
        value: {
          fontSize: '11px',
          fontWeight: 600,
          color: 'var(--bc-ink-3)',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          lineHeight: 1,
        },
      },
      'bc-caption': {
        value: {
          fontSize: '12px',
          color: 'var(--bc-ink-3)',
          lineHeight: 1.4,
        },
      },
      'bc-data': {
        value: {
          fontFamily: 'var(--bc-mono)',
          fontSize: '12px',
          color: 'var(--bc-ink)',
          lineHeight: 1.4,
        },
      },
      'bc-heading-sm': {
        value: {
          fontSize: '13px',
          fontWeight: 600,
          color: 'var(--bc-ink)',
          lineHeight: 1.3,
        },
      },
      'bc-body': {
        value: {
          fontSize: '13px',
          color: 'var(--bc-ink)',
          lineHeight: 1.5,
        },
      },
    },

    recipes: {
      code: defineRecipe({
        variants: {
          variant: {
            inModal: {
              whiteSpace: 'pre-wrap',
            },
          },
        },
      }),
      badge: defineRecipe({
        variants: {
          variant: {
            // Generic bc-themed badge (unit labels, tags)
            bc: {
              bg: 'var(--bc-surface-2)',
              border: '1px solid var(--bc-line)',
              color: 'var(--bc-ink-2)',
              borderRadius: 'var(--bc-r-sm)',
              fontSize: '11px',
              fontWeight: 500,
              px: '6px',
              py: '2px',
            },
            // Status badge: set data-status="active|warn|offline" on the element
            bcStatus: {
              borderRadius: 'var(--bc-r-sm)',
              fontSize: '11px',
              fontWeight: 600,
              px: '6px',
              py: '2px',
              '&[data-status="active"]': {
                bg: 'var(--bc-green-tint)',
                color: 'var(--bc-ink)',
              },
              '&[data-status="warn"]': {
                bg: 'var(--bc-amber-tint)',
                color: 'var(--bc-ink)',
              },
              '&[data-status="offline"]': {
                bg: 'var(--bc-red-tint)',
                color: 'var(--bc-ink)',
              },
            },
          },
        },
      }),
      button: defineRecipe({
        base: {
          fontWeight: 'semibold',
          lineHeight: '1.2',
        },
        variants: {
          size: {
            md: {
              textStyle: 'md',
            },
          },
          variant: {
            solid: {
              _hover: {
                bg: 'colorPalette.600',
              },
            },
            link: {
              _hover: {
                transform: 'scale(1.75)',
              },
              minWidth: 'unset',
              color: '#1A202C',
              _dark: {
                color: 'rgba(255, 255, 255, 0.92)',
              },
            },
          },
        },
      }),
    },

    slotRecipes: {
      table: defineSlotRecipe({
        slots: ['root', 'header', 'body', 'row', 'columnHeader', 'cell'],
        variants: {
          variant: {
            bc: {
              root: {
                bg: 'var(--bc-surface)',
                // NB: no `overflow`/`borderRadius` here. An overflow clip on the
                // <table> makes it the scroll-context for position:sticky cells,
                // which breaks the pinned actions column. The rounded-corner clip
                // lives on the scrolling wrapper Box in Table.component instead.
              },
              header: {
                bg: 'var(--bc-surface-2)',
                borderBottom: '1px solid var(--bc-line)',
              },
              columnHeader: {
                bg: 'var(--bc-surface-2)',
                borderColor: 'var(--bc-line)',
                fontSize: '11px',
                fontWeight: 600,
                color: 'var(--bc-ink-3)',
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                py: '8px',
                px: '12px',
              },
              cell: {
                borderColor: 'var(--bc-line)',
                fontSize: '13px',
                color: 'var(--bc-ink)',
                py: '10px',
                px: '12px',
              },
              row: {
                _hover: { bg: 'var(--bc-surface-2)' },
                _last: { td: { borderBottom: 'none' } },
              },
            },
          },
        },
      }),
      radioGroup: defineSlotRecipe({
        slots: ['item'],
        base: {
          item: {
            cursor: 'pointer',
            _disabled: {
              cursor: 'not-allowed',
            },
          },
        },
      }),
      accordion: defineSlotRecipe({
        slots: [
          'root',
          'item',
          'itemTrigger',
          'itemContent',
          'itemBody',
          'itemIndicator',
        ],
        base: {
          itemTrigger: {
            // v3 adds fontWeight: "medium" which changes accordion header
            // text from v1's default (normal). Reset to inherit.
            fontWeight: 'inherit',
            cursor: 'pointer',
            _hover: {
              bg: 'gray.100',
              _dark: { bg: 'gray.600' },
            },
          },
        },
      }),
      dialog: defineSlotRecipe({
        slots: [
          'trigger',
          'backdrop',
          'positioner',
          'content',
          'title',
          'description',
          'closeTrigger',
          'header',
          'body',
          'footer',
        ],
        variants: {
          variant: {
            alwaysOpen: {
              content: {
                pointerEvents: 'auto',
              },
              positioner: {
                pointerEvents: 'none',
              },
            },
            minimized: {
              content: {
                pointerEvents: 'auto',
              },
              positioner: {
                pointerEvents: 'none',
                display: 'none',
              },
            },
          },
        },
      }),
      drawer: defineSlotRecipe({
        slots: [
          'trigger',
          'backdrop',
          'positioner',
          'content',
          'title',
          'description',
          'closeTrigger',
          'header',
          'body',
          'footer',
        ],
        variants: {
          variant: {
            alwaysOpen: {
              content: {
                pointerEvents: 'auto',
              },
              positioner: {
                pointerEvents: 'none',
              },
            },
          },
        },
      }),
    },
  },
})

export const system = createSystem(defaultConfig, config)
